import React, {useState, useEffect} from "react";
import TextInput from '../../Form/TextInput.tsx';
import { IProfile } from '../../../interface/IProfile.ts';
import { useProfileApi } from '../../../features/auth/hooks/use-profile-api.ts'
import { ApiResponseType } from "../../../features/api/types.ts";
import Select from '../../Form/Select.tsx';
import { iso3166 } from "../../../static/iso3166.js";
import Form from "../../Form/Form.tsx";
import { useValidators } from "../../../features/functions/validators.tsx";
import { useHelper } from "../../../features/functions/helper.tsx";

const TabPrivateData = ({
    profile,
    setProfile
}:{
    profile:IProfile
    setProfile: Function
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string>('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');
    const { postProfile } = useProfileApi();
    const [errors, setErrors] = useState<any>({});
    const [unSavedChanges, setUnSavedChanges] = useState(false);
    const [hasInvalidForm, setHasInvalidForm] = useState(false);

    const [formData, setFormData] = useState<IProfile>({
        private_address: profile.private_address ?? '',
        private_zip: profile.private_zip ?? '',
        private_city: profile.private_city ?? '',
        private_country: profile.private_country ?? '',
        private_phone: profile.private_phone ?? '',
        private_mobile: profile.private_mobile ?? '',
        private_email: profile.private_email ?? '',
        private_web: profile.private_web ?? '',
        private_linkedin: profile.private_linkedin ?? '',
    });

    const { validateEmail, validateUrl, validateLinkedinUrl, validatePhone } = useValidators();
    const { checkProfileFieldsForErrors } = useHelper();

    const handleChange = (event, name = '', value = null) => {
        setFormData({ ...formData, [name ? name : event.target.name]: value ? value : event.target.value });
        !unSavedChanges && setUnSavedChanges(true);
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
        !unSavedChanges && setUnSavedChanges(true);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setUnSavedChanges(false);
        setIsLoading(true);

        let formDataClone = structuredClone(formData);
        if (profile.preferred_contact_address_source === 'private') {
            formDataClone.contact_address = formDataClone.private_address;
            formDataClone.contact_city = formDataClone.private_city;
            formDataClone.contact_zip = formDataClone.private_zip;
            formDataClone.contact_country = formDataClone.private_country;
        }
        if (profile.preferred_contact_email_source === 'private_email') {
            formDataClone.contact_email = formDataClone.private_email;
        }
        if (profile.preferred_contact_phone_source === 'private_mobile') {
            formDataClone.contact_phone = formDataClone.private_mobile;
        }
        if (profile.preferred_contact_phone_source === 'private_phone') {
            formDataClone.contact_phone = formDataClone.private_phone;
        }

        setErrors({});
        Object.keys(formDataClone).forEach(k => formDataClone[k] = typeof formDataClone[k] === 'string' ? formDataClone[k].trim() : formDataClone[k]); 

        postProfile(formDataClone).then(res => {
            setIsLoading(false);
            if (res.type === ApiResponseType.OK) {
                if (res.payload.success)  {
                    setResponseMessage('Erfolgreich gespeichert');
                    setResponseMessageClass('success');
                    setProfile({...profile, ...formDataClone});
                    setTimeout(() => {
                        setResponseMessage('');
                    }, 5000);
                    setHasInvalidForm(false);
                }
            } else {
                let errors = res.payload.errors!;
                if (!errors.general_error) {
                    setResponseMessage('Fehler. Bitte korrigieren Sie den Fehler und versuchen Sie es erneut.');
                    setResponseMessageClass('error');
                }
                setErrors(errors);
            }
        })
    }

    useEffect(() => {
        let fieldsToCheck = {
            'private_address': 'text',
            'private_zip': 'text',
            'private_city': 'text',
            'private_country': 'text',
            'private_email': 'email',
        };
        if (profile.private_web && profile.private_web.length > 0) {
            fieldsToCheck['private_web'] = 'url';
        }
        if (profile.private_linkedin && profile.private_linkedin.length > 0) {
            fieldsToCheck['private_linkedin'] = 'linkedin';
        }
        let errors = checkProfileFieldsForErrors(profile, fieldsToCheck);
        if ((!profile.private_phone || profile.private_phone.length <= 0) && (!profile.private_mobile || profile.private_mobile.length <= 0)) {
            errors['private_phone'] = 'Festnetznummer oder Mobilnummer muss angegeben werden';
            errors['private_mobile'] = 'Festnetznummer oder Mobilnummer muss angegeben werden';
        }
        if (profile.private_phone && profile.private_phone.length > 0) {
            let privatePhoneErrors:any = checkProfileFieldsForErrors(profile, {'private_phone': 'phone'});
            if (privatePhoneErrors.private_phone) {
                errors['private_phone'] = privatePhoneErrors.private_phone;
            }
        }
        if (profile.private_mobile && profile.private_mobile.length > 0) {
            let privatePhoneErrors:any = checkProfileFieldsForErrors(profile, {'private_mobile': 'phone'});
            if (privatePhoneErrors.private_mobile) {
                errors['private_mobile'] = privatePhoneErrors.private_mobile;
            }
        }
        if (Object.keys(errors).length > 0) {
            setHasInvalidForm(true);
            setErrors(errors);
        }
    }, [])

    return (
        <div className="single-tab-item">
            <Form
                unSavedChanges={unSavedChanges}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                responseMessage={responseMessage}
                responseMessageClass={responseMessageClass}
                generalErrors={ errors.general_error ? errors.general_error : null}
                displayRequiredHint={true}
                hasInvalidForm={hasInvalidForm}
            >
                <TextInput
                    onChange={handleChange}
                    value={formData?.private_address}
                    label='Straße, Hausnummer'
                    required={true}
                    name='private_address'
                    error={errors['private_address']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData?.private_zip}
                    label='PLZ'
                    required={true}
                    name='private_zip'
                    error={errors['private_zip']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData?.private_city}
                    label='Ort'
                    required={true}
                    name='private_city'
                    error={errors['private_city']}
                />
                <Select
                    isSearchable={true}
                    isClearable={true}
                    isRequired={true}
                    label='Land'
                    placeholder='Land auswählen'
                    name='privateCountry'
                    onChange={(e, action) => {
                        if (e) {
                            handleSelectChange('private_country', e.value)
                        }
                        if (!e && action.action === 'clear') {
                            handleSelectChange('private_country', '')
                        }
                    }}
                    defaultValue={formData?.private_country}
                    options={iso3166}
                    error={errors['private_country']}
                />
                <TextInput
                    onChange={(e) => {
                        handleChange(e);
                        let errorClone = {...errors}
                        let phoneValid = validatePhone(e.target.value, false, 'Festnetznummer');
                        if (phoneValid === true) {
                            delete errorClone['private_phone'];
                            setErrors(errorClone);
                        } else {
                            errorClone['private_phone'] = phoneValid;
                            setErrors(errorClone)
                        }
                    }}
                    value={formData?.private_phone}
                    label='Festnetz'
                    required={formData?.private_mobile === ''}
                    name='private_phone'
                    type='tel'
                    error={errors['private_phone']}
                    placeholder="+49123456789123"
                />
                <TextInput
                    onChange={(e) => {
                        handleChange(e);
                        let errorClone = {...errors}
                        let phoneValid = validatePhone(e.target.value, false, 'Mobilnummer');
                        if (phoneValid === true) {
                            delete errorClone['private_mobile'];
                            setErrors(errorClone);
                        } else {
                            errorClone['private_mobile'] = phoneValid;
                            setErrors(errorClone)
                        }
                    }}
                    value={formData?.private_mobile}
                    label='Mobil'
                    required={formData?.private_phone === ''}
                    name='private_mobile'
                    type='tel'
                    error={errors['private_mobile']}
                    placeholder="+49123456789123"
                />
                <TextInput
                    onChange={(e) => {
                        handleChange(e);
                        let errorClone = {...errors}
                        if (!validateEmail(e.target.value)) {
                            errorClone['private_email'] = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
                            setErrors(errorClone)
                        } else {
                            delete errorClone['private_email'];
                            setErrors(errorClone);
                        }
                    }}
                    value={formData?.private_email}
                    label='E-Mail'
                    required={true}
                    name='private_email'
                    type='email'
                    error={errors['private_email']}
                    placeholder="max.mustermann@fpsb.de"
                />
                <TextInput
                    onChange={(e) => {
                        handleChange(e);
                        let errorClone = {...errors}
                        if (e.target.value.length > 0 && !validateUrl(e.target.value)) {
                            errorClone['private_web'] = 'Bitte geben Sie eine gültige URL im Format http:// oder https:// ein.';
                            setErrors(errorClone)
                        } else {
                            delete errorClone['private_web'];
                            setErrors(errorClone);
                        }
                    }}
                    value={formData?.private_web}
                    label='Internet'
                    name='private_web'
                    error={errors['private_web']}
                    type='text'
                    placeholder="https://www.fpsb.de/"
                />
                <TextInput
                    onChange={(e) => {
                        handleChange(e);
                        let errorClone = {...errors}
                        if (e.target.value.length > 0 && !validateLinkedinUrl(e.target.value)) {
                            errorClone['private_linkedin'] = 'Bitte geben Sie eine gültige LinkedIn URL im Format http:// oder https:// ein.';
                            setErrors(errorClone)
                        } else {
                            delete errorClone['private_linkedin'];
                            setErrors(errorClone);
                        }
                    }}
                    value={formData?.private_linkedin}
                    label='LinkedIn - Profilname'
                    name='private_linkedin'
                    error={errors['private_linkedin']}
                    type='text'
                    placeholder="https://www.linkedin.com/company/financial-planning-standards-board-ltd-/"
                />
            </Form>
        </div>
    )
}

export default TabPrivateData;
